// src/components/asset-manager/SearchBar.tsx
import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Search, X, Loader2 } from 'lucide-react';
import { useDebounce } from './hooks/useDebounce';

export interface SearchBarRef {
  clearSearch: () => void;
}

interface SearchBarProps {
  onSearch: (query: string) => void;
  onReset?: () => void;
  isSearching?: boolean;
  placeholder?: string;
}

export const SearchBar = forwardRef<SearchBarRef, SearchBarProps>(({ 
  onSearch, 
  onReset,
  isSearching = false,
  placeholder = "Search files and folders..."
}, ref) => {
  const [inputValue, setInputValue] = useState('');

  // Use debounce hook with 500ms delay
  const debouncedValue = useDebounce(inputValue, 500);

  useImperativeHandle(ref, () => ({
    clearSearch: () => {
      setInputValue('');
      onReset?.();
    }
  }));

  // Effect to trigger search on debounced value change
  useEffect(() => {
    onSearch(debouncedValue);
  }, [debouncedValue, onSearch]);

  const handleReset = () => {
    setInputValue('');
    onReset?.();
  };

  return (
    <form onSubmit={(e) => e.preventDefault()} className="relative w-full sm:w-[300px]">
      <div className="relative">
        <Search 
          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" 
          size={18} 
        />
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder={placeholder}
          className={`pl-10 pr-10 w-full placeholder-gray-400
          `}
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center space-x-2">
          {isSearching && (
            <Loader2 className="w-4 h-4 animate-spin text-primary-500" />
          )}
          {inputValue && (
            <button
              type="button"
              onClick={handleReset}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={16} />
            </button>
          )}
        </div>
      </div>
    </form>
  );
});